import { collectionGroup, getDocs, writeBatch } from "firebase/firestore";
import dayjs from "dayjs";
import { db } from "../firebaseService/connection";
import logger from "../../utils/logger";

// One-off backfill for `details.hasStartTime` on content saved before the flag
// existed. Covers groupAdminPost/{groupId}/posts/* and parentGroups/{id}/events/*.
//
// Rule (same as the dashboard's read fallback):
//   no eventStartDate, or timestamp at exactly 00:00 local  -> hasStartTime: false
//   any other timestamp                                     -> hasStartTime: true
// Documents that already carry a boolean flag are left untouched.
//
// Usage from the browser console in a non-production build, logged in as a
// user with write access to those collections:
//   await window.backfillHasStartTime()                 // dry run, logs counts
//   await window.backfillHasStartTime({ apply: true })  // writes

export const resolveHasStartTime = (details) => {
  const ts = details?.eventStartDate;
  if (ts === null || ts === undefined || ts === "" || Number(ts) === 0) {
    return false;
  }
  const value = dayjs.unix(Number(ts));
  return !(value.hour() === 0 && value.minute() === 0);
};

const BATCH_LIMIT = 450;

export async function backfillHasStartTime({ apply = false } = {}) {
  const [postsSnap, eventsSnap] = await Promise.all([
    getDocs(collectionGroup(db, "posts")),
    getDocs(collectionGroup(db, "events")),
  ]);

  const candidates = [...postsSnap.docs, ...eventsSnap.docs].filter((d) => {
    const path = d.ref.path;
    return (
      path.startsWith("groupAdminPost/") || path.startsWith("parentGroups/")
    );
  });

  const updates = [];
  let alreadyFlagged = 0;
  candidates.forEach((d) => {
    const details = d.data()?.details;
    if (typeof details?.hasStartTime === "boolean") {
      alreadyFlagged += 1;
      return;
    }
    updates.push({ ref: d.ref, value: resolveHasStartTime(details) });
  });

  const summary = {
    scanned: candidates.length,
    alreadyFlagged,
    toUpdate: updates.length,
    willBeTrue: updates.filter((u) => u.value).length,
    willBeFalse: updates.filter((u) => !u.value).length,
    applied: false,
  };

  if (apply) {
    for (let i = 0; i < updates.length; i += BATCH_LIMIT) {
      const batch = writeBatch(db);
      updates
        .slice(i, i + BATCH_LIMIT)
        .forEach(({ ref, value }) =>
          batch.update(ref, { "details.hasStartTime": value }),
        );
      await batch.commit();
    }
    summary.applied = true;
  }

  logger.log("backfillHasStartTime", summary);
  console.table(summary);
  return summary;
}
